import { IdToken, useAuth0 } from '@auth0/auth0-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../application/hooks';
import { Account, FeatureSet } from '@lemontaps/lib/types/api';
import { generateStaticTierInfo } from '@lemontaps/lib/util';
import { FeatureFlagContext } from './useFeatureFlags';

export default function useTierInfo() {
  const { getIdTokenClaims, isAuthenticated } = useAuth0();
  const [idToken, setIdToken] = useState<IdToken | null>(null);
  useEffect(() => {
    if (isAuthenticated && !idToken) {
      getIdTokenClaims().then(token => setIdToken(token as IdToken));
    }
  }, [idToken, getIdTokenClaims, isAuthenticated]);

  const account = useAppSelector<Account>(state => state.account);
  const theme = account?.theme;

  const isFeatureAllowed = useCallback(
    (feature: keyof FeatureSet) => !!theme?.featureSet?.[feature],
    [theme?.featureSet],
  );

  const isMinTier = useCallback(
    (minTier: string) => {
      if (!theme?.themeInternal?.tier) return false;
      if (minTier === 'starter') return true;
      else if (minTier === 'essentials')
        return ['essentials', 'pro', 'enterprise'].includes(theme?.themeInternal?.tier);
      else if (minTier === 'pro') ['pro', 'enterprise'].includes(theme?.themeInternal?.tier);
      else if (minTier === 'enterprise') return theme?.themeInternal?.tier === 'enterprise';
      else return false;
    },
    [theme?.themeInternal?.tier],
  );

  const staticTierInfo = useMemo(
    () => generateStaticTierInfo(account, theme, idToken),
    [account, idToken, theme],
  );
  const isDbLoading = useAppSelector(state => state.login.isLoading);

  const context = useContext(FeatureFlagContext);

  const tierInfo = useMemo(
    () => ({
      ...staticTierInfo,
      isFeatureAllowed,
      isMinTier,
      privateFlags: context.privateFlags,
      publicFlags: context.publicFlags,
      loading: isDbLoading,
    }),
    [
      context?.privateFlags,
      context?.publicFlags,
      isFeatureAllowed,
      isMinTier,
      staticTierInfo,
      isDbLoading,
    ],
  );

  return tierInfo;
}

export type TierInfo = ReturnType<typeof useTierInfo>;
